import React, { useEffect, useState } from "react";
/* eslint-disable react/no-unescaped-entities */
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ApiKeysSection } from "@/components/ApiKeysSection";
import { AlertSettingsSection } from "@/components/AlertSettingsSection";
import { baseAsset } from "@/lib/utils";

const Field = ({ label, value, onChange, hint, step = "1", id, disabled = false }) => (
    <div className="flex flex-col gap-1.5">
        <Label htmlFor={id} className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">{label}</Label>
        <input
            id={id}
            type="number"
            step={step}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            className={`border border-[#1B2A3A] rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#2EE6D6] ${
                disabled
                    ? "bg-[#0B0F14] text-[#6F7D92] cursor-not-allowed"
                    : "bg-[#0B0F14] text-[#E6EDF6]"
            }`}
            data-testid={id}
        />
        {hint && <div className="text-[10px] text-[#6F7D92]">{hint}</div>}
    </div>
);

export const SettingsDialog = ({ open, onOpenChange, initial = {}, autoSizing = null, walletAllocation = null, onSave, isRunning = false, bot = "main", onAlertPrefsChange = null, hideGridSize = false }) => {
    const base = baseAsset(initial.symbol);
    // Legacy deployments stored one per-side percent in fee_rate_pct. Present
    // it through the canonical manual fields so saving the dialog migrates the
    // value instead of silently replacing it with auto-mode zeros.
    const legacyFeePct = Number(initial.fee_rate_pct ?? 0);
    const hasCanonicalFee = Number(initial.buy_fee_pct ?? 0) > 0
        || Number(initial.sell_fee_pct ?? 0) > 0
        || Number(initial.buy_fee_flat_usdt ?? 0) > 0
        || Number(initial.sell_fee_flat_usdt ?? 0) > 0;
    const initialFeeMode = !hasCanonicalFee && legacyFeePct > 0
        ? "manual" : (initial.fee_mode ?? "auto");
    const initialBuyFeePct = !hasCanonicalFee && legacyFeePct > 0
        ? legacyFeePct : (initial.buy_fee_pct ?? 0);
    const initialSellFeePct = !hasCanonicalFee && legacyFeePct > 0
        ? legacyFeePct : (initial.sell_fee_pct ?? 0);
    const [grid, setGrid] = useState(initial.grid_size ?? 250);
    const [bq, setBq] = useState(initial.buy_qty ?? 0.0313);
    const [sq, setSq] = useState(initial.sell_qty ?? 0.0313);
    const [mb, setMb] = useState(initial.max_buys ?? 10);
    const [autoMax, setAutoMax] = useState(initial.auto_max_buys ?? true);
    const [hardCap, setHardCap] = useState(initial.max_buys_hard_cap ?? 200);
    const [cap, setCap] = useState(initial.btc_inventory_cap ?? 1.0);
    const [reserve, setReserve] = useState(initial.usdt_reserve ?? 100);
    const [allocMode, setAllocMode] = useState(initial.wallet_alloc_mode ?? "full");
    const [allocValue, setAllocValue] = useState(initial.wallet_alloc_value ?? 100);
    const [tif, setTif] = useState(initial.tif ?? "GTX");
    const [cancelBuys, setCancelBuys] = useState(false);
    const [feeMode, setFeeMode] = useState(initialFeeMode);
    const [buyFeePct, setBuyFeePct] = useState(initialBuyFeePct);
    const [sellFeePct, setSellFeePct] = useState(initialSellFeePct);
    const [buyFeeFlat, setBuyFeeFlat] = useState(initial.buy_fee_flat_usdt ?? 0);
    const [sellFeeFlat, setSellFeeFlat] = useState(initial.sell_fee_flat_usdt ?? 0);
    const [armCushion, setArmCushion] = useState(initial.exit_arm_cushion_pct ?? 0);
    const [structAll, setStructAll] = useState(initial.exit_structure_all_sells ?? true);
    const [pumpMode, setPumpMode] = useState(initial.pump_strategy_mode ?? "protect");
    const [pumpQtyMult, setPumpQtyMult] = useState(initial.pump_chase_qty_mult ?? 0.5);
    const [pumpLive, setPumpLive] = useState(initial.pump_chase_live_enabled ?? false);
    const [daltonLive, setDaltonLive] = useState(initial.dalton_live_entry_enabled ?? false);

    useEffect(() => {
        if (open) {
            setGrid(initial.grid_size ?? 250);
            setBq(initial.buy_qty ?? 0.0313);
            setSq(initial.sell_qty ?? 0.0313);
            setMb(initial.max_buys ?? 10);
            setAutoMax(initial.auto_max_buys ?? true);
            setHardCap(initial.max_buys_hard_cap ?? 200);
            setCap(initial.btc_inventory_cap ?? 1.0);
            setReserve(initial.usdt_reserve ?? 100);
            setAllocMode(initial.wallet_alloc_mode ?? "full");
            setAllocValue(initial.wallet_alloc_value ?? 100);
            setTif(initial.tif ?? "GTX");
            setCancelBuys(false);
            setFeeMode(initialFeeMode);
            setBuyFeePct(initialBuyFeePct);
            setSellFeePct(initialSellFeePct);
            setBuyFeeFlat(initial.buy_fee_flat_usdt ?? 0);
            setSellFeeFlat(initial.sell_fee_flat_usdt ?? 0);
            setArmCushion(initial.exit_arm_cushion_pct ?? 0);
            setStructAll(initial.exit_structure_all_sells ?? true);
            setPumpMode(initial.pump_strategy_mode ?? "protect");
            setPumpQtyMult(initial.pump_chase_qty_mult ?? 0.5);
            setPumpLive(initial.pump_chase_live_enabled ?? false);
            setDaltonLive(initial.dalton_live_entry_enabled ?? false);
        }
    }, [open, initial, initialFeeMode, initialBuyFeePct, initialSellFeePct]);

    const handleSave = () => {
        onSave({
            // SGB grid spacing is AI-managed — never send it from that page.
            ...(hideGridSize ? {} : { grid_size: Number(grid) }),
            buy_qty: Number(bq),
            sell_qty: Number(sq),
            max_buys: Number(mb),
            auto_max_buys: Boolean(autoMax),
            max_buys_hard_cap: Number(hardCap),
            btc_inventory_cap: Number(cap),
            usdt_reserve: Number(reserve),
            wallet_alloc_mode: allocMode,
            wallet_alloc_value: Number(allocValue),
            tif,
            cancel_open_buys: Boolean(cancelBuys),
            fee_mode: feeMode,
            buy_fee_pct: Number(buyFeePct),
            sell_fee_pct: Number(sellFeePct),
            buy_fee_flat_usdt: Number(buyFeeFlat),
            sell_fee_flat_usdt: Number(sellFeeFlat),
            exit_arm_cushion_pct: Number(armCushion),
            exit_structure_all_sells: Boolean(structAll),
            pump_strategy_mode: pumpMode,
            pump_chase_qty_mult: Number(pumpQtyMult),
            pump_chase_live_enabled: Boolean(pumpLive),
            dalton_live_entry_enabled: Boolean(daltonLive),
        });
    };

    // Live preview of the auto-sized value (from /status auto_sizing block).
    const previewMax = autoSizing?.auto_max_buys;
    const previewBreakdown =
        autoSizing && autoSizing.per_order_notional
            ? `Using $${Number(autoSizing.usdt_free || 0).toFixed(2)} free − $${Number(
                  autoSizing.usdt_reserve || 0
              ).toFixed(2)} reserve = $${Number(autoSizing.usable_usdt || 0).toFixed(
                  2
              )} usable @ $${Number(autoSizing.per_order_notional || 0).toFixed(2)}/order → up to ${
                  autoSizing.auto_max_buys
              } rungs (cap ${autoSizing.hard_cap || hardCap})`
            : "Waiting for first price tick to compute…";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="panel max-w-xl" data-testid="settings-dialog">
                <DialogHeader>
                    <DialogTitle>Strategy Settings</DialogTitle>
                    <DialogDescription>
                        Adjust grid spacing, order sizing and safety limits. Changes apply on the next tick.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4 py-2">
                    {hideGridSize ? (
                        <div className="flex flex-col gap-1" data-testid="settings-grid-ai-note">
                            <span className="text-[11px] uppercase tracking-wider text-[#6F7D92]">Grid Size ($)</span>
                            <div className="border border-[#1B2A3A] rounded-md px-3 py-2 text-sm font-mono bg-[#0B0F14] text-[#2EE6D6]">
                                AI-managed
                            </div>
                            <div className="text-[10px] text-[#6F7D92]">
                                Spacing adapts to the market regime automatically.
                            </div>
                        </div>
                    ) : (
                        <Field
                            id="settings-grid-size"
                            label="Grid Size ($)"
                            value={grid}
                            onChange={setGrid}
                            step="1"
                            hint="Spacing between BUYs and BUY→SELL profit."
                        />
                    )}
                    <Field
                        id="settings-buy-qty"
                        label={`Buy Qty (${base})`}
                        value={bq}
                        onChange={setBq}
                        step="0.0001"
                        hint="Size of each BUY. Drives the auto max-rung calc."
                    />
                    <Field
                        id="settings-sell-qty"
                        label={`Sell Qty (${base})`}
                        value={sq}
                        onChange={setSq}
                        step="0.0001"
                    />
                    <Field
                        id="settings-btc-cap"
                        label={`${base} Inventory Cap`}
                        value={cap}
                        onChange={setCap}
                        step="0.01"
                        hint={`Stop opening new BUYs above this ${base} holding.`}
                    />
                    <Field
                        id="settings-usdt-reserve"
                        label="USDT Reserve (floor)"
                        value={reserve}
                        onChange={setReserve}
                        step="10"
                        hint="The bot will NEVER dip below this."
                    />
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">
                            Time in Force
                        </Label>
                        <select
                            value={tif}
                            onChange={(e) => setTif(e.target.value)}
                            className="bg-[#0B0F14] border border-[#1B2A3A] rounded-md px-3 py-2 text-sm font-mono text-[#E6EDF6] focus:outline-none focus:border-[#2EE6D6]"
                            data-testid="settings-tif"
                        >
                            <option value="GTX">GTX (Post-Only)</option>
                            <option value="GTC">GTC</option>
                            <option value="IOC">IOC</option>
                            <option value="FOK">FOK</option>
                        </select>
                    </div>
                </div>

                {/* Fees: auto (learned from fills) or manual % / flat per fill */}
                <div className="rounded-md border border-[#1B2A3A] p-3 mb-2" data-testid="settings-fees">
                    <div className="flex items-center justify-between">
                        <Label className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">
                            Fees
                        </Label>
                        <select
                            value={feeMode}
                            onChange={(e) => setFeeMode(e.target.value)}
                            className="bg-[#0B0F14] border border-[#1B2A3A] rounded-md px-2 py-1 text-xs font-mono text-[#E6EDF6] focus:outline-none focus:border-[#2EE6D6]"
                            data-testid="settings-fee-mode"
                        >
                            <option value="auto">Auto — learned from fills</option>
                            <option value="manual">Manual</option>
                        </select>
                    </div>
                    <div className="text-[10px] text-[#6F7D92] mt-1">
                        Manual: percent per fill takes precedence; when % is 0 the flat USDT amount per fill is used.
                        Feeds the fee-aware grid floor and paper-mode fill costs.
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-2" style={{ opacity: feeMode === "manual" ? 1 : 0.4 }}>
                        <Field id="settings-buy-fee-pct" label="Buy Fee (%)" value={buyFeePct}
                            onChange={setBuyFeePct} step="0.01" disabled={feeMode !== "manual"} />
                        <Field id="settings-sell-fee-pct" label="Sell Fee (%)" value={sellFeePct}
                            onChange={setSellFeePct} step="0.01" disabled={feeMode !== "manual"} />
                        <Field id="settings-buy-fee-flat" label="Buy Fee flat (USDT/fill)" value={buyFeeFlat}
                            onChange={setBuyFeeFlat} step="0.01" disabled={feeMode !== "manual"} />
                        <Field id="settings-sell-fee-flat" label="Sell Fee flat (USDT/fill)" value={sellFeeFlat}
                            onChange={setSellFeeFlat} step="0.01" disabled={feeMode !== "manual"} />
                    </div>
                    {/* E-H7: server-computed effective terms — same FeePolicy the
                        execution path uses; this component only formats numbers. */}
                    {initial?.fee_preview && (
                        <div className="text-[10px] text-[#A7B4C6] mt-2 font-mono" data-testid="settings-fee-preview">
                            {initial.fee_preview.source === "unobserved"
                                ? "Effective fees: not yet learned (no fills observed)"
                                : <>
                                    Effective: buy {Number(initial.fee_preview.buy_fee_pct).toFixed(4)}% ·
                                    sell {Number(initial.fee_preview.sell_fee_pct).toFixed(4)}% ·
                                    round-trip {Number(initial.fee_preview.round_trip_pct).toFixed(4)}%
                                    {" "}({initial.fee_preview.source})
                                    {initial.fee_preview.breakeven_sell
                                        ? <> · breakeven sell ≈ {Number(initial.fee_preview.breakeven_sell).toFixed(2)}</>
                                        : null}
                                </>}
                        </div>
                    )}
                </div>

                {/* Wallet allocation cap — how much of the REAL wallet the bot may use */}
                <div
                    className="rounded-md p-3 mt-2"
                    style={{ background: "rgba(46,230,214,0.05)", border: "1px solid rgba(46,230,214,0.25)" }}
                    data-testid="settings-wallet-alloc-panel"
                >
                    <div className="text-[12px] font-semibold text-[#E6EDF6]">Wallet Allocation</div>
                    <div className="text-[10px] text-[#A7B4C6] mt-0.5 mb-3">
                        Cap how much of your real wallet this bot treats as its balance. Reserve and
                        available are calculated on this considered amount. e.g. 50% of a $50k wallet → bot uses $25k.
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="flex flex-col gap-1.5">
                            <Label className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">Mode</Label>
                            <select
                                value={allocMode}
                                onChange={(e) => setAllocMode(e.target.value)}
                                className="bg-[#0B0F14] border border-[#1B2A3A] rounded-md px-3 py-2 text-sm font-mono text-[#E6EDF6] focus:outline-none focus:border-[#2EE6D6]"
                                data-testid="settings-wallet-alloc-mode"
                            >
                                <option value="full">Full wallet (no cap)</option>
                                <option value="pct">Percentage of wallet</option>
                                <option value="absolute">Absolute USDT amount</option>
                            </select>
                        </div>
                        <Field
                            id="settings-wallet-alloc-value"
                            label={allocMode === "absolute" ? "Amount (USDT)" : "Percent (%)"}
                            value={allocValue}
                            onChange={setAllocValue}
                            step={allocMode === "absolute" ? "100" : "1"}
                            disabled={allocMode === "full"}
                            hint={allocMode === "full" ? "Disabled — using the entire wallet."
                                : allocMode === "pct" ? "0–100% of the real wallet balance."
                                : "Bot considers min(real wallet, this)."}
                        />
                    </div>
                    {walletAllocation && (
                        <div className="text-[10px] text-[#6F7D92] font-mono mt-2" data-testid="settings-wallet-alloc-preview">
                            Real wallet ${Number(walletAllocation.real_total_usdt || 0).toFixed(2)} →
                            considered ${Number(walletAllocation.considered_total_usdt || 0).toFixed(2)}
                            (available ${Number(walletAllocation.available_usdt || 0).toFixed(2)} after reserve)
                        </div>
                    )}
                </div>

                {/* Exit policy & fees (SGB ExitManager) */}
                <div
                    className="rounded-md p-3 mt-2"
                    style={{ background: "rgba(255,176,32,0.05)", border: "1px solid rgba(255,176,32,0.25)" }}
                    data-testid="settings-exit-panel"
                >
                    <div className="text-[12px] font-semibold text-[#E6EDF6]">Exit &amp; Fees</div>
                    <div className="text-[10px] text-[#A7B4C6] mt-0.5 mb-3">
                        One trailing stop style (bfcc): once price clears buy + fees + 2×cushion,
                        the stop is born at buy + fees + cushion and trails price 1:1 from there.
                        It only ratchets up and never sits below buy + fees + cushion.
                    </div>
                    <div className="grid grid-cols-2 gap-3 items-end">
                        <Field
                            id="settings-arm-cushion-pct"
                            label="Cushion (%)"
                            value={armCushion}
                            onChange={setArmCushion}
                            step="0.01"
                            hint="Sizes the bfcc levels: stop born at buy+fees+cushion; trail arms at buy+fees+2×cushion. 0 uses the minimum 0.05% cushion."
                        />
                        <label className="flex items-start gap-3 cursor-pointer pb-2"
                            data-testid="settings-struct-all-row">
                            <Switch
                                checked={structAll}
                                onCheckedChange={setStructAll}
                                data-testid="settings-struct-all-toggle"
                            />
                            <div className="leading-tight">
                                <div className="text-[12px] font-semibold text-[#E6EDF6]">Trailing SL on ALL sells</div>
                                <div className="text-[10px] text-[#A7B4C6] mt-0.5">
                                    Every filled buy gets the bfcc trailing stop above; the classic grid
                                    take-profit stays in place. Off = classic fixed TP only for
                                    non-zone fills.
                                </div>
                            </div>
                        </label>
                    </div>
                </div>

                <div
                    className="rounded-md p-3 mt-2"
                    style={{ background: "rgba(46,230,214,0.04)", border: "1px solid rgba(46,230,214,0.22)" }}
                    data-testid="settings-pump-strategy-panel"
                >
                    <div className="text-[12px] font-semibold text-[#E6EDF6] mb-2">Confirmed Pump Strategy</div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="flex flex-col gap-1.5 col-span-2">
                            <Label htmlFor="settings-pump-mode" className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">
                                Behavior
                            </Label>
                            <select
                                id="settings-pump-mode"
                                value={pumpMode}
                                onChange={(e) => setPumpMode(e.target.value)}
                                className="bg-[#0B0F14] border border-[#1B2A3A] rounded-md px-3 py-2 text-sm font-mono text-[#E6EDF6] focus:outline-none focus:border-[#2EE6D6]"
                                data-testid="settings-pump-mode"
                            >
                                <option value="protect">Protect holdings only — do not chase</option>
                                <option value="chase">One confirmed chase + shifting SL and TP</option>
                            </select>
                            <div className="text-[10px] text-[#6F7D92]">
                                Chase needs positive 1m/5m/15m momentum and leader confirmation: +0.8%/15m normally, +1.2%/15m under a macro-bear backdrop. Knife/crash always blocks. Vetoes are logged as PUMP_BLOCKED in the action feed. Paper/testnet by default — LIVE needs the opt-in below.
                            </div>
                        </div>
                        <Field
                            id="settings-pump-chase-qty"
                            label="Chase size (× normal buy)"
                            value={pumpQtyMult}
                            onChange={setPumpQtyMult}
                            step="0.1"
                            disabled={pumpMode !== "chase"}
                            hint="0.1×–1.0×; reserve and inventory caps still apply."
                        />
                    </div>
                </div>

                <div
                    className="rounded-md p-3 mt-2"
                    style={{ background: "rgba(255,92,108,0.05)", border: "1px solid rgba(255,92,108,0.35)" }}
                    data-testid="settings-live-entries-panel"
                >
                    <div className="text-[12px] font-semibold text-[#FF5C6C] mb-1">Live-mode strategy entries</div>
                    <div className="text-[10px] text-[#A7B4C6] mb-2">
                        Momentum chase and Dalton zone entries normally run in paper/testnet only.
                        These opt a LIVE bot in. Crash veto, inventory cap/reserve and the kill
                        switch still apply.
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer mb-2" data-testid="settings-pump-live-row">
                        <Checkbox
                            checked={pumpLive}
                            onCheckedChange={(v) => setPumpLive(Boolean(v))}
                            className="mt-0.5"
                            data-testid="settings-pump-live-checkbox"
                        />
                        <div className="leading-tight">
                            <div className="text-[12px] text-[#E6EDF6]">Allow Pump Chase entries on LIVE</div>
                        </div>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer" data-testid="settings-dalton-live-row">
                        <Checkbox
                            checked={daltonLive}
                            onCheckedChange={(v) => setDaltonLive(Boolean(v))}
                            className="mt-0.5"
                            data-testid="settings-dalton-live-checkbox"
                        />
                        <div className="leading-tight">
                            <div className="text-[12px] text-[#E6EDF6]">Allow Dalton zone entry management on LIVE</div>
                            <div className="text-[10px] text-[#A7B4C6] mt-0.5">
                                Requires the Support-Zone strategy (strat_zone_entry) to be enabled.
                            </div>
                        </div>
                    </label>
                </div>

                {/* Behavior A: cancel previous grid's unfilled BUYs on save */}
                <label
                    className="flex items-start gap-3 rounded-md p-3 mt-1 cursor-pointer"
                    style={{ background: "rgba(255,92,108,0.05)", border: "1px solid rgba(255,92,108,0.22)" }}
                    data-testid="settings-cancel-buys-row"
                >
                    <Checkbox
                        checked={cancelBuys}
                        onCheckedChange={(v) => setCancelBuys(Boolean(v))}
                        className="mt-0.5"
                        data-testid="settings-cancel-buys-checkbox"
                    />
                    <div className="leading-tight">
                        <div className="text-[12px] font-semibold text-[#E6EDF6]">
                            Cancel previous grid's unfilled BUY orders
                        </div>
                        <div className="text-[10px] text-[#A7B4C6] mt-0.5">
                            On save, cancel all open BUYs so the new grid size has a clean slate.
                            Open SELLs are never touched (filled positions stay protected). Leave
                            unticked to keep old BUYs — the bot will auto-cancel the farthest rung
                            if capital is needed for the top of the grid.
                        </div>
                    </div>
                </label>

                {/* Auto-max-buys panel */}
                <div
                    className="rounded-md p-3 mt-2"
                    style={{
                        background: "rgba(46,230,214,0.05)",
                        border: "1px solid rgba(46,230,214,0.25)",
                    }}
                    data-testid="settings-auto-max-panel"
                >
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <div className="text-[12px] font-semibold text-[#E6EDF6]">
                                Auto-size Max BUYs
                            </div>
                            <div className="text-[10px] text-[#A7B4C6] mt-0.5">
                                Number of BUY rungs is derived from wallet balance and order size.
                            </div>
                        </div>
                        <Switch
                            checked={autoMax}
                            onCheckedChange={setAutoMax}
                            data-testid="settings-auto-max-toggle"
                        />
                    </div>
                    {autoMax ? (
                        <div className="space-y-2">
                            <div className="flex items-baseline gap-2">
                                <span className="text-[10px] uppercase tracking-[0.14em] text-[#A7B4C6]">
                                    Computed max BUYs
                                </span>
                                <span
                                    className="font-mono text-base text-[#2EE6D6]"
                                    data-testid="settings-auto-max-value"
                                >
                                    {previewMax ?? "—"}
                                </span>
                            </div>
                            <div className="text-[10px] text-[#6F7D92] leading-relaxed font-mono">
                                {previewBreakdown}
                            </div>
                            <div className="grid grid-cols-2 gap-3 mt-3">
                                <Field
                                    id="settings-max-buys-hard-cap"
                                    label="Hard cap (sanity ceiling)"
                                    value={hardCap}
                                    onChange={setHardCap}
                                    step="1"
                                    hint="Auto-sizing will never exceed this number."
                                />
                                <Field
                                    id="settings-max-buys"
                                    label="Manual Max BUYs (ignored)"
                                    value={mb}
                                    onChange={setMb}
                                    step="1"
                                    disabled
                                    hint="Turn Auto OFF to edit manually."
                                />
                            </div>
                        </div>
                    ) : (
                        <Field
                            id="settings-max-buys"
                            label="Manual Max BUYs"
                            value={mb}
                            onChange={setMb}
                            step="1"
                            hint={
                                previewMax != null
                                    ? `(Wallet currently supports up to ${previewMax} rungs.)`
                                    : ""
                            }
                        />
                    )}
                </div>

                <AlertSettingsSection onChange={onAlertPrefsChange} />

                <ApiKeysSection bot={bot} isRunning={isRunning} />

                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        data-testid="settings-cancel-button"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        className="bg-[#2EE6D6] hover:bg-[#43F0E0] text-[#0B0F14]"
                        data-testid="settings-save-button"
                    >
                        Save
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default SettingsDialog;
